/* ─── CART DRAWER ───────────────────────────────────────────────────────────
   Aggiornamento del carrello con UNA sola richiesta per azione: le chiamate
   /cart/change|clear.js includono il parametro `sections` e Shopify rispedisce
   già l'HTML renderizzato della sezione cart-drawer (Sections Rendering API),
   così la lista si aggiorna subito senza fetch extra e senza riaprire la tendina.
   L'aggiunta al carrello (product-page.js) passa la sezione via evento cart:updated.

   Lingua: `sections_url` è sempre il path della pagina corrente (/it/...), così
   Shopify renderizza le sezioni nella lingua del cliente. Il refresh di
   sicurezza passa dalla home localizzata SENZA header Accept: sulle pagine
   prodotto/carrello quell'header fa rispondere Shopify col JSON della risorsa
   invece che con le sezioni.

   Tastiera: da chiuso il pannello è `inert` (fuori dal Tab); da aperto il focus
   va sul pulsante chiudi, resta intrappolato nel drawer, ESC chiude e il focus
   torna al pulsante che l'aveva aperto. */
const cartSectionsBase = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
// Endpoint cart col prefisso lingua (/it/cart/change.js): così anche i messaggi
// d'errore di Shopify ("Sono disponibili solo 3 pezzi") arrivano in italiano.
const cartUrl = (path) => cartSectionsBase() + path;

// Errore cart con la spiegazione di Shopify ("Sono disponibili solo 2 pezzi"):
// è già nella lingua del cliente, quindi va mostrata così com'è.
async function cartError(res) {
  let msg = '';
  try { const j = await res.json(); msg = j.description || j.message || ''; } catch (_) {}
  const err = new Error(msg || `HTTP ${res.status}`);
  err.userMessage = msg;
  return err;
}

function showCartError(scope, err) {
  const box = scope && scope.querySelector('[data-cart-error]');
  if (!box) return;
  if (!err) { box.hidden = true; box.textContent = ''; return; }
  box.textContent = (err && err.userMessage) || box.dataset.fallback || '';
  box.hidden = false;
}
class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;
    this.overlay = document.querySelector('.cart-drawer-overlay');
    this.panel = this.drawer.querySelector('.cart-drawer-container');
    this.trigger = null;
    // Ultimo elemento con focus FUORI dal drawer: all'apertura dopo un'aggiunta
    // il pulsante "Aggiungi" è disabilitato e il focus è già scivolato sul body.
    this.lastFocus = null;
    document.addEventListener('focusin', (e) => { if (!this.drawer.contains(e.target)) this.lastFocus = e.target; });
    // Lock anti tap-tap: una sola azione cart in volo alla volta. Senza,
    // due +/− rapidi lanciano richieste parallele che si sorpassano e il
    // drawer "torna indietro" al render della risposta arrivata per ultima.
    this.busy = false;
    this.init();
  }

  init() {
    if (this.panel) this.panel.inert = true;
    this.drawer.addEventListener('keydown', (e) => this.onKey(e));
    document.querySelectorAll('[data-cart-drawer-toggle]').forEach(btn => {
      btn.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
    });
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());

    // Delegati su document: i bottoni dentro al drawer vengono ricreati ad ogni
    // aggiornamento, quindi NON possiamo bindare direttamente sui singoli nodi.
    document.addEventListener('click', (e) => {
      const close = e.target.closest('[data-cart-drawer-close]');
      if (close) { e.preventDefault(); this.close(); return; }
      const qty = e.target.closest('[data-qty-change]');
      if (qty) { e.preventDefault(); this.updateQuantity(qty.dataset.qtyChange, qty.dataset.qtyAction); return; }
      // Solo DENTRO il drawer: [data-cart-remove] esiste anche nelle righe
      // della pagina /cart, dove lo gestisce CartPage. Senza questo vincolo
      // una rimozione da /cart faceva partire DUE change.js in parallelo, con
      // drawer e pagina renderizzati da risposte diverse.
      const rem = e.target.closest('[data-cart-drawer] [data-cart-remove]');
      if (rem) { e.preventDefault(); this.removeItem(rem.dataset.cartRemove); return; }
      const clr = e.target.closest('[data-cart-clear]');
      if (clr) { e.preventDefault(); this.clearCart(); return; }
    });

    // Aggiunta da product-page.js (o altrove): se l'evento porta già la sezione
    // renderizzata, la usiamo all'istante; altrimenti facciamo un refresh.
    // detail.silent = true (es. dalla pagina /cart): aggiorna drawer+conteggio ma
    // NON aprire il drawer (l'azione è avvenuta sulla pagina carrello, non sul drawer).
    document.addEventListener('cart:updated', (e) => {
      const detail = (e && e.detail) || {};
      const sections = detail.sections;
      if (sections && sections['cart-drawer']) {
        this.renderDrawer(sections['cart-drawer']);
        this.syncCountFromDrawer();
        if (!detail.silent) this.open();
      } else if (!detail.silent) {
        this.refreshDrawer().then(() => this.open());
      }
    });
  }

  isOpen() { return !!this._open; }

  // Blocco della pagina e animazione in due fotogrammi diversi: bloccare lo
  // scroll (body position:fixed) ricalcola l'intera pagina, e farlo nello
  // stesso fotogramma in cui parte lo scorrimento del pannello ne mangiava
  // l'inizio (su Safari lo scatto si vedeva). In chiusura lo sblocco aspetta
  // la fine dell'animazione per lo stesso motivo.
  open() {
    if (this.isOpen()) return;
    this._open = true;
    clearTimeout(this._unlockTimer);
    const a = document.activeElement;
    this.trigger = a && a !== document.body && !this.drawer.contains(a) ? a : this.lastFocus;
    if (this.panel) this.panel.inert = false;
    window.GuyDom.lockScroll('cart-drawer');
    requestAnimationFrame(() => { if (this._open) this.drawer.classList.add('active'); });
    const btn = this.drawer.querySelector('.cart-drawer-close');
    if (btn) btn.focus({ preventScroll: true });
  }

  close() {
    if (!this.isOpen()) return;
    this._open = false;
    this.drawer.classList.remove('active');
    this._unlockTimer = setTimeout(() => { if (!this._open) window.GuyDom.unlockScroll('cart-drawer'); }, 420);
    showCartError(this.drawer, null);
    if (this.panel) this.panel.inert = true;
    // Il pulsante "Aggiungi" resta disabilitato ~2s dopo l'aggiunta e un
    // bottone disabilitato non prende il focus: riproviamo finché si riattiva.
    const t = this.trigger;
    const back = (n) => {
      if (!t || !document.contains(t) || this.isOpen()) return;
      if (!t.disabled) { t.focus({ preventScroll: true }); return; }
      if (n > 0) setTimeout(() => back(n - 1), 200);
    };
    back(15);
    this.trigger = null;
  }

  onKey(e) {
    if (!this.isOpen()) return;
    if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
    if (e.key !== 'Tab' || !this.panel) return;
    const f = [...this.panel.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),textarea,select,[tabindex]:not([tabindex="-1"])')]
      .filter(el => el.offsetParent !== null);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  // POST a un endpoint cart chiedendo nella stessa richiesta la sezione cart-drawer.
  async cartPost(url, payload) {
    const body = Object.assign({ sections: 'cart-drawer', sections_url: window.location.pathname }, payload || {});
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!res.ok) throw await cartError(res);
    return res.json();
  }

  // Esegue un'azione cart sotto lock: i click durante la richiesta vengono
  // ignorati (feedback: classe is-busy sul drawer). Su errore il drawer si
  // risincronizza dal server, così non resta mai un numero sbagliato a video.
  async run(fn) {
    if (this.busy) return;
    this.busy = true;
    this.drawer.classList.add('is-busy');
    showCartError(this.drawer, null);
    try {
      await fn();
      // Azione fatta dal drawer mentre si è su /cart: la pagina si riallinea.
      document.dispatchEvent(new CustomEvent('cart:changed', { detail: { source: 'drawer' } }));
    }
    catch (e) { console.error('Cart error:', e); await this.refreshDrawer(); showCartError(this.drawer, e); }
    finally { this.busy = false; this.drawer.classList.remove('is-busy'); }
  }

  async updateQuantity(key, action) {
    const input = document.querySelector(`[data-qty-input="${key}"]`);
    if (!input) return;
    let qty = parseInt(input.value, 10);
    if (action === 'plus') qty++;
    else if (action === 'minus' && qty > 1) qty--;
    else if (action === 'minus' && qty <= 1) { this.removeItem(key); return; }
    await this.run(async () => this.applyCart(await this.cartPost(cartUrl('cart/change.js'), { id: key, quantity: qty })));
  }

  async removeItem(key) {
    await this.run(async () => this.applyCart(await this.cartPost(cartUrl('cart/change.js'), { id: key, quantity: 0 })));
  }

  async clearCart() {
    await this.run(async () => this.applyCart(await this.cartPost(cartUrl('cart/clear.js'), {})));
  }

  // Refresh del solo contenuto del drawer (usato come fallback).
  async refreshDrawer() {
    try {
      const res = await fetch(`${cartSectionsBase()}?sections=cart-drawer`);
      if (!res.ok) return;
      const sections = await res.json();
      if (sections['cart-drawer']) { this.renderDrawer(sections['cart-drawer']); this.syncCountFromDrawer(); }
    } catch (_) {}
  }

  // change/clear restituiscono il cart completo (+ sections): aggiorniamo conteggio
  // dal cart (autorevole) e contenuto dalla sezione renderizzata.
  applyCart(cart) {
    if (cart && typeof cart.item_count === 'number') this.setCount(cart.item_count);
    if (cart && cart.sections && cart.sections['cart-drawer']) {
      this.renderDrawer(cart.sections['cart-drawer']);
      if (typeof cart.item_count !== 'number') this.syncCountFromDrawer();
    } else {
      this.refreshDrawer();
    }
  }

  renderDrawer(html) {
    const content = document.querySelector('[data-cart-drawer-content]');
    if (!content) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const fresh = tmp.querySelector('[data-cart-drawer-content]');
    if (fresh) content.innerHTML = fresh.innerHTML;
  }

  setCount(n) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = n;
      el.style.display = n > 0 ? 'flex' : 'none';
    });
  }

  // Quando non abbiamo il cart (es. add), ricaviamo il conteggio sommando le
  // quantità degli item nel drawer appena renderizzato.
  syncCountFromDrawer() {
    let n = 0;
    document.querySelectorAll('[data-cart-drawer-content] [data-qty-input]').forEach(i => {
      n += parseInt(i.value, 10) || 0;
    });
    this.setCount(n);
  }
}

/* ─── PAGINA CARRELLO (/cart) ────────────────────────────────────────────────
   Stesse regole del drawer (Legge 2): qty/rimozione via /cart/change.js con
   `sections=main,cart-drawer` nella stessa richiesta; il contenuto della pagina
   (`[data-cart-page-content]`) è rimpiazzato dalla sezione `main` renderizzata, e
   l'evento `cart:updated` (silent) aggiorna header + drawer senza aprirlo.
   Niente form POST, niente reload. La nota si salva su /cart/update.js. */
class CartPage {
  constructor() {
    this.page = document.querySelector('[data-cart-page]');
    if (!this.page) return;
    // ID reale della sezione (template--123__main): la chiave "main" del
    // template JSON non è accettata dalla Sections Rendering API.
    this.sectionId = this.page.dataset.sectionId || 'main';
    this.busy = false;
    this.page.addEventListener('change', (e) => {
      const qty = e.target.closest('[data-cart-qty]');
      if (qty) { let v = parseInt(qty.value, 10); if (isNaN(v) || v < 0) v = 0; this.change(qty.dataset.cartQty, v); return; }
      const note = e.target.closest('[data-cart-note]');
      if (note) { fetch(cartUrl('cart/update.js'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) }); }
    });
    this.page.addEventListener('click', (e) => {
      const rem = e.target.closest('[data-cart-remove]');
      if (rem) { e.preventDefault(); this.change(rem.dataset.cartRemove, 0); }
    });
    // Modifiche fatte dal drawer: la tabella della pagina non deve restare indietro.
    document.addEventListener('cart:changed', (e) => {
      if (e.detail && e.detail.source === 'drawer') this.refresh();
    });
  }

  render(html) {
    if (!html) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const fresh = tmp.querySelector('[data-cart-page-content]');
    const cur = this.page.querySelector('[data-cart-page-content]');
    if (fresh && cur) cur.innerHTML = fresh.innerHTML;
  }

  async refresh() {
    try {
      const res = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(this.sectionId)}`);
      if (!res.ok) return;
      const secs = await res.json();
      this.render(secs[this.sectionId]);
    } catch (_) {}
  }

  async change(key, qty) {
    if (this.busy) return;
    this.busy = true; this.page.classList.add('is-busy');
    showCartError(this.page, null);
    try {
      const res = await fetch(cartUrl('cart/change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty, sections: `${this.sectionId},cart-drawer`, sections_url: window.location.pathname })
      });
      if (!res.ok) throw await cartError(res);
      const cart = await res.json();
      const secs = cart.sections || {};
      this.render(secs[this.sectionId]);
      // header + drawer in sync, ma senza aprire il drawer (silent)
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { sections: secs, silent: true } }));
    } catch (err) {
      // Niente reload: il cliente perderebbe il motivo. Riallineiamo la tabella
      // (la quantità richiesta non è passata) e mostriamo la spiegazione.
      console.error('Cart page error:', err);
      await this.refresh();
      showCartError(this.page, err);
    } finally {
      this.busy = false; this.page.classList.remove('is-busy');
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => { new CartDrawer(); new CartPage(); });
} else {
  new CartDrawer(); new CartPage();
}
